import { z } from 'zod';
/**
 * Moderation (ADR 0037). An admin names a player and a sanction from their
 * room; the server checks the account's flag again, writes the sanction and
 * applies it wherever the player is. Any player may report another; admins
 * read the reports over HTTP.
 */
const NameSchema = z.string().trim().min(1).max(32);
const ReasonSchema = z
    .string()
    .transform((text) => text
    .replace(/\s+/g, ' ')
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .trim())
    .pipe(z.string().max(200))
    .optional();
/** A week at most: longer is a ban. */
export const MUTE_MAX_MINUTES = 7 * 24 * 60;
export const BAN_MAX_DAYS = 3650;
export const ModerateMessageSchema = z.discriminatedUnion('action', [
    z
        .object({
        action: z.literal('mute'),
        name: NameSchema,
        minutes: z.number().int().min(1).max(MUTE_MAX_MINUTES).default(10),
        reason: ReasonSchema,
    })
        .strict(),
    z.object({ action: z.literal('unmute'), name: NameSchema }).strict(),
    z.object({ action: z.literal('kick'), name: NameSchema, reason: ReasonSchema }).strict(),
    z
        .object({
        action: z.literal('ban'),
        name: NameSchema,
        /** Absent: for good. */
        days: z.number().int().min(1).max(BAN_MAX_DAYS).optional(),
        reason: ReasonSchema,
    })
        .strict(),
    z.object({ action: z.literal('unban'), name: NameSchema }).strict(),
]);
export const SANCTION_KINDS = ['mute', 'kick', 'ban'];
export const SanctionKindSchema = z.enum(SANCTION_KINDS);
/** To the admin: what was done, to whom, until when (server time in ms; null for good or at once). */
export const ModerationResultSchema = z.object({
    action: z.enum(['mute', 'unmute', 'kick', 'ban', 'unban']),
    name: z.string().min(1),
    until: z.number().int().positive().nullable(),
});
export const MODERATION_REFUSALS = [
    'invalid',
    'not-admin',
    /** No character of that name exists. */
    'not-found',
    'self',
    /** Admins are not sanctioned from the game. */
    'admin-target',
    /** A kick needs the player in the game. */
    'offline',
    /** Nothing to lift. */
    'no-sanction',
];
export const ModerationRejectedSchema = z.object({ reason: z.enum(MODERATION_REFUSALS) });
/** To a muted player who tried to speak, and at join while a mute stands: server time it ends. */
export const MutedEventSchema = z.object({ until: z.number().int().positive() });
/**
 * Reports. Any player, about any character, once a minute at most
 * (`REPORT_COOLDOWN_MS`); admins read them from the game master's window.
 */
export const PLAYER_REPORT_REASONS = ['spam', 'harcelement', 'triche', 'nom', 'autre'];
/**
 * Every reason a report row may carry: what players choose, and
 * `remboursement`, filed by the shop itself when a refunded purchase had
 * already left the buyer (ADR 0044).
 */
export const REPORT_REASONS = [...PLAYER_REPORT_REASONS, 'remboursement'];
export const ReportReasonSchema = z.enum(REPORT_REASONS);
export const REPORT_COOLDOWN_MS = 60_000;
export const REPORT_DETAILS_MAX = 500;
export const ReportMessageSchema = z.object({
    name: NameSchema,
    reason: z.enum(PLAYER_REPORT_REASONS),
    details: z
        .string()
        .transform((text) => text
        .replace(/\s+/g, ' ')
        .replace(/[\p{Cc}\p{Cf}]/gu, '')
        .trim())
        .pipe(z.string().max(REPORT_DETAILS_MAX))
        .optional(),
});
export const ReportResultSchema = z.object({ name: z.string().min(1) });
export const REPORT_REFUSALS = ['invalid', 'not-found', 'self', 'too-soon'];
export const ReportRejectedSchema = z.object({ reason: z.enum(REPORT_REFUSALS) });
/** A report as the admins' window lists it. */
export const ReportRowSchema = z.object({
    id: z.uuid(),
    reporterName: z.string().min(1),
    targetName: z.string().min(1),
    reason: ReportReasonSchema,
    details: z.string().nullable(),
    status: z.enum(['open', 'closed']),
    createdAt: z.string().min(1),
});
export const ReportsResponseSchema = z.object({ reports: z.array(ReportRowSchema) });
/** A sanction as the admins' window lists it. */
export const SanctionRowSchema = z.object({
    id: z.uuid(),
    kind: SanctionKindSchema,
    targetName: z.string().min(1).nullable(),
    until: z.string().min(1).nullable(),
    reason: z.string().nullable(),
    byName: z.string().min(1).nullable(),
    createdAt: z.string().min(1),
    liftedAt: z.string().min(1).nullable(),
});
export const SanctionsResponseSchema = z.object({ sanctions: z.array(SanctionRowSchema) });
/** The gold of the game as an admin reads it (ADR 0038). */
export const GoldFlowSchema = z.object({
    minted: z.record(z.string(), z.number().int().min(0)),
    burnt: z.record(z.string(), z.number().int().min(0)),
    transferred: z.record(z.string(), z.number().int().min(0)),
    net: z.number().int(),
});
export const EconomyReportSchema = z.object({
    /** Gold in every purse together. */
    supply: z.number().int().min(0),
    /** Purses holding at least one coin. */
    purses: z.number().int().min(0),
    lastDay: GoldFlowSchema,
    lastWeek: GoldFlowSchema,
});
//# sourceMappingURL=moderation.js.map